// Centralized Modules Registry (single source of truth)
// Responsibilities:
// - Fetch and cache user modules with TTL keyed by userEmail+orgId+role
// - Fetch and cache organization modules (for DEMO flag detection)
// - Normalize module codes for consistent lookups
// - Provide permission checking (isModuleAuthorized)
// - Deduplicate concurrent identical requests
// - Expose simple imperative API and a React hook for components
//
// Notes:
// - No external deps; lightweight singleton store
// - All UI text/comments in English per project standards

import { useEffect, useMemo, useState } from 'react';

type Module = {
  id: number;
  name: string;
  code?: string;
  is_basic?: boolean;
  isExtraModule?: boolean;
  originalCode?: string;
  [key: string]: any;
};

type ModulesListMeta = {
  cacheKey: string;
  fetchedAt: number;
  expiresAt: number;
};

type OrgModulesMeta = {
  orgId: number;
  modules: Module[];
  fetchedAt: number;
  expiresAt: number;
};

// TTLs (adjustable)
const MODULES_LIST_TTL_MS = 2 * 60 * 1000; // 2 minutes
const ORG_MODULES_TTL_MS = 2 * 60 * 1000; // 2 minutes

// Module code normalization maps
const nameToCodeMap: Record<string, string> = {
  'clerks': 'clerks',
  'agentes': 'clerks',
  'agents': 'clerks',
  'dashboard': 'dashboard',
  'panel principal': 'dashboard',
  'panel': 'dashboard',
  'dynamic dashboard': 'dynamic_dashboard',
  'dashboard dinámico': 'dynamic_dashboard',
  'panel dinámico': 'dynamic_dashboard',
  'data assistant': 'data_assistant',
  'asistente de datos': 'data_assistant',
  'bella vibe': 'bella_vibe',
  'bella_vibe': 'bella_vibe',
  'vibe': 'bella_vibe',
  'knowledge base': 'knowledge_base',
  'base de conocimiento': 'knowledge_base',
  'ingestion': 'knowledge_base',
  'users': 'users',
  'usuarios': 'users',
  'user management': 'users',
  'administración de usuarios': 'users',
  'autonomous action plans': 'autonomous_action_plans',
  'planes de acción autónomos': 'autonomous_action_plans',
  'health plan recommendation': 'health_plan_recommendation',
  'health plan recomendation': 'health_plan_recommendation',
  'shpe': 'health_plan_recommendation',
  'plan de salud': 'health_plan_recommendation',
  'recomendación de planes': 'health_plan_recommendation',
  'demo video': 'demo_video',
  'demo video generator': 'demo_video',
  'heygen': 'demo_video'
};

const codeAliases: Record<string, string[]> = {
  'clerks': ['agents', 'agents_admin', 'agent_management', 'clerk_management'],
  'users': ['user_management', 'user', 'user_admin', 'usuarios'],
  'knowledge_base': ['ingestion', 'knowledge_management', 'kb', 'base_conocimiento'],
  'data_assistant': ['assistant', 'data_assistant', 'chat', 'chatbot'],
  'bella_vibe': ['bella_vibe', 'vibe', 'voice_interface', 'voice_assistant'],
  'autonomous_action_plans': ['autonomous_plan', 'action_plans', 'aab'],
  'prompt_builder': ['intelligent_prompt_builder', 'prompt_generator', 'ai_prompt_builder'],
  'dashboard': ['panel', 'home', 'inicio'],
  'dynamic_dashboard': ['panel_dinamico', 'dynamic_panel'],
  'health_plan_recommendation': ['shpe', 'health_plan', 'plan_recommendation', 'health_plan_recommendation'],
  'demo_video': ['demo_video', 'heygen', 'video_generator', 'demo_video_generator'],
  'document_pipeline': ['document_pipeline', 'entity_extraction', 'ocr', 'pipeline_extraction', 'doc_pipeline']
};

const moduleAliases: Record<string, string[]> = {
  'clerks': ['agents', 'agents_admin', 'agent_management', 'clerk_management'],
  'users': ['user_management', 'user', 'user_admin'],
  'knowledge_base': ['ingestion', 'knowledge_management', 'kb'],
  'autonomous_action_plans': ['autonomous_plan', 'action_plans', 'aab'],
  'tables_management': ['tables', 'db_tables', 'database_tables', 'table_management'],
  'health_plan_recommendation': ['shpe', 'health_plan', 'plan_recommendation'],
  'demo_video': ['demo_video', 'heygen', 'video_generator'],
  'document_pipeline': ['document_pipeline', 'entity_extraction', 'ocr', 'pipeline_extraction']
};

// Internal store state
const state = {
  userModules: [] as Module[],
  orgModules: [] as Module[],
  listMeta: null as ModulesListMeta | null,
  orgModulesCache: new Map<number, OrgModulesMeta>(),
  inFlight: new Map<string, Promise<any>>(),
  subscribers: new Set<() => void>(),
  loading: false,
  error: null as string | null,
};

function notify() {
  state.subscribers.forEach((cb) => {
    try { cb(); } catch {}
  });
}

function now() {
  return Date.now();
}

// getAuthContext() function removed - it was obsolete and no longer used
// All functions now require options to be passed from React context (useModules hook)

function computeListCacheKey(options?: { orgId?: number; role?: string; email?: string }): string {
  // Options should always be provided from React context (useModules hook)
  // No fallback to localStorage - options are required
  const email = options?.email || 'unknown';
  const orgId = options?.orgId || 0;
  const role = options?.role || 'user';
  return `${email}|${orgId}|${role}`;
}

function normalizeModuleCodes(modules: Module[]): Module[] {
  return modules.map(module => {
    const originalModule = {...module};
    let normalized = false;
    
    // First check exact code matches
    if (module.code) {
      const exactCode = module.code.toLowerCase();
      if (exactCode === 'dashboard') {
        module.code = 'dashboard';
        normalized = true;
      } else if (exactCode === 'dynamic_dashboard') {
        module.code = 'dynamic_dashboard';
        normalized = true;
      }
    }
    
    // If no exact normalization, proceed with normal algorithm
    if (!normalized) {
      // Search by name if exists
      if (module.name) {
        const lowerName = module.name.toLowerCase();
        
        // Exact match has priority
        if (lowerName === 'dynamic dashboard' || lowerName === 'panel dinámico') {
          module.code = 'dynamic_dashboard';
          module.originalCode = originalModule.code;
          normalized = true;
        } else if (lowerName === 'dashboard' || lowerName === 'panel principal') {
          module.code = 'dashboard';
          module.originalCode = originalModule.code;
          normalized = true;
        } else {
          // Search for partial name matches
          for (const [key, value] of Object.entries(nameToCodeMap)) {
            if (lowerName.includes(key.toLowerCase())) {
              module.code = value;
              module.originalCode = originalModule.code;
              normalized = true;
              break;
            }
          }
        }
      }
      
      // If module already has a code, check if it needs normalization
      if (!normalized && module.code) {
        const lowerCode = module.code.toLowerCase();
        
        // Search in aliases
        for (const [standardCode, aliases] of Object.entries(codeAliases)) {
          if (lowerCode === standardCode.toLowerCase() || aliases.some(alias => lowerCode === alias.toLowerCase())) {
            module.code = standardCode;
            module.originalCode = originalModule.code;
            normalized = true;
            break;
          }
        }
      }
    }
    
    return module;
  });
}

function isListFresh(options?: { orgId?: number; role?: string; email?: string }): boolean {
  if (!state.listMeta) return false;
  const desiredKey = computeListCacheKey(options);
  if (state.listMeta.cacheKey !== desiredKey) return false;
  return state.listMeta.expiresAt > now();
}

function isOrgModulesFresh(orgId: number): boolean {
  const entry = state.orgModulesCache.get(orgId);
  return !!entry && entry.expiresAt > now();
}

async function dedupedFetch(key: string, fn: () => Promise<any>): Promise<any> {
  const existing = state.inFlight.get(key);
  if (existing) return existing;
  const p = fn().finally(() => {
    state.inFlight.delete(key);
  });
  state.inFlight.set(key, p);
  return p;
}

export const ModulesRegistry = {
  subscribe(cb: () => void) {
    state.subscribers.add(cb);
    return () => state.subscribers.delete(cb);
  },

  getState() {
    return {
      userModules: state.userModules,
      orgModules: state.orgModules,
      loading: state.loading,
      error: state.error,
      listMeta: state.listMeta,
    };
  },

  async loadModules(force = false, options?: { orgId?: number; role?: string; email?: string }): Promise<Module[]> {
    // Options should always be provided from React context (useModules hook)
    // No fallback to localStorage - options are required
    const orgId = options?.orgId;
    const role = options?.role;
    const email = options?.email;
    
    // Recompute cache key with the actual orgId being used
    const desiredKey = `${email || 'unknown'}|${orgId || 0}|${role || 'user'}`;
    const currentKey = state.listMeta?.cacheKey;
    const cacheKeyChanged = desiredKey !== currentKey;

    console.log(`[ModulesRegistry] loadModules called: force=${force}, cacheKeyChanged=${cacheKeyChanged}, desiredKey=${desiredKey}, currentKey=${currentKey}`);
    console.log(`[ModulesRegistry] Using orgId from options (React context): ${orgId}`);
    console.log(`[ModulesRegistry] Using email from options (React context): ${email}`);

    // Use isListFresh with options to ensure consistent cache key comparison
    if (!force && !cacheKeyChanged && isListFresh(options)) {
      console.log(`[ModulesRegistry] Using cached modules (fresh)`);
      return state.userModules;
    }

    console.log(`[ModulesRegistry] Auth context: email=${email}, orgId=${orgId}, role=${role}`);
    
    if (!email) {
      console.warn('[ModulesRegistry] No email found, clearing modules and setting loading=false');
      state.userModules = [];
      state.orgModules = [];
      state.loading = false; // CRITICAL: Asegurar que loading es false cuando no hay email
      state.error = null;
      notify();
      return [];
    }
    
    if (!orgId || orgId === 0) {
      console.warn('[ModulesRegistry] No valid orgId found, cannot load organization modules');
    }

    state.loading = true;
    state.error = null;
    notify();
    
    // Timeout de seguridad: si loadModules tarda más de 10 segundos, establecer loading=false
    // Esto previene que ProtectedRoute se quede bloqueado indefinidamente
    const loadingTimeout = setTimeout(() => {
      if (state.loading) {
        console.warn('[ModulesRegistry] loadModules timeout after 10s, setting loading=false');
        state.loading = false;
        state.error = 'Timeout loading modules';
        notify();
      }
    }, 10000);

    try {
      // Dynamic imports to avoid circular dependencies
      const UserService = (await import('../services/user.service')).default;
      const OrganizationService = (await import('../services/organization.service')).default;

      const isSuperUser = role === 'super_user';
      let userModules: Module[] = [];

      // If super_user, try to get all modules
      if (isSuperUser) {
        try {
          const allModules = await dedupedFetch(
            `GET|modules/all|${email}`,
            () => UserService.getAllModules()
          );
          if (Array.isArray(allModules) && allModules.length > 0) {
            userModules = allModules;
          }
        } catch (superUserError) {
          console.warn('[ModulesRegistry] Error getting all modules for super_user:', superUserError);
        }
      }

      // Normal flow for all users
      if (userModules.length === 0) {
        userModules = await dedupedFetch(
          `GET|user-modules|${email}|${orgId}`,
          () => UserService.getUserModules(email)
        );
      }

      // Get organization modules for DEMO flag detection and authorization
      let orgModules: Module[] = [];
      if (orgId) {
        try {
          if (isOrgModulesFresh(orgId)) {
            orgModules = state.orgModulesCache.get(orgId)!.modules;
            console.log(`[ModulesRegistry] Using cached org modules for org ${orgId}:`, orgModules.length, 'modules');
          } else {
            console.log(`[ModulesRegistry] Fetching org modules for org ${orgId}...`);
            orgModules = await dedupedFetch(
              `GET|org-modules|${orgId}`,
              () => OrganizationService.getOrganizationModules(orgId)
            );
            console.log(`[ModulesRegistry] Fetched ${orgModules.length} modules for org ${orgId}:`, orgModules.map(m => m.code || m.name).join(', '));
            state.orgModulesCache.set(orgId, {
              orgId,
              modules: orgModules,
              fetchedAt: now(),
              expiresAt: now() + ORG_MODULES_TTL_MS,
            });
          }
        } catch (orgError) {
          console.error('[ModulesRegistry] Error getting organization modules for org', orgId, ':', orgError);
          // Don't fail completely, but log the error
        }
      } else {
        console.warn('[ModulesRegistry] No orgId provided, cannot load organization modules');
      }

      // Normalize organization modules first (for authorization checks)
      console.log(`[ModulesRegistry] Before normalization - orgModules:`, orgModules.length, 'modules');
      const normalizedOrgModules = normalizeModuleCodes(orgModules);
      console.log(`[ModulesRegistry] After normalization - orgModules:`, normalizedOrgModules.length, 'modules');
      console.log(`[ModulesRegistry] Normalized org module codes:`, normalizedOrgModules.map(m => m.code || m.name).join(', '));

      // Mark modules as DEMO (isExtraModule) if not in organization
      const orgModuleIds = new Set(orgModules.map(m => m.id));
      const userModulesWithDemoFlag = userModules.map(module => ({
        ...module,
        isExtraModule: !orgModuleIds.has(module.id) && orgModuleIds.size > 0
      }));

      // Normalize user module codes
      const normalizedUserModules = normalizeModuleCodes(userModulesWithDemoFlag);

      // Store separately: userModules (what user can access) vs orgModules (what org has enabled)
      state.userModules = normalizedUserModules;
      state.orgModules = normalizedOrgModules;
      console.log(`[ModulesRegistry] Final state - userModules:`, normalizedUserModules.length, ', orgModules:', normalizedOrgModules.length);
      state.listMeta = {
        cacheKey: desiredKey,
        fetchedAt: now(),
        expiresAt: now() + MODULES_LIST_TTL_MS,
      };
      clearTimeout(loadingTimeout);
      state.loading = false;
      notify();
      return normalizedUserModules;
    } catch (e: any) {
      clearTimeout(loadingTimeout);
      state.loading = false;
      state.error = e?.message || 'Failed to load modules';
      state.userModules = [];
      state.orgModules = [];
      notify();
      return [];
    }
  },

  isModuleAuthorized(moduleCode: string | undefined, role?: string, orgId?: number): boolean {
    console.log("🔍 [ModulesRegistry] === isModuleAuthorized LLAMADO ===");
    console.log("🔍 [ModulesRegistry] moduleCode:", moduleCode);
    console.log("🔍 [ModulesRegistry] role pasado como parámetro:", role);
    console.log("🔍 [ModulesRegistry] orgId pasado como parámetro:", orgId);
    
    // Role should always be provided as parameter (from useModules hook)
    // No fallback to getAuthContext() - it's obsolete
    const userRole = role || 'user';
    const orgModules = state.orgModules;

    console.log("🔍 [ModulesRegistry] === RESOLUCIÓN DE ROL ===");
    console.log("🔍 [ModulesRegistry] role desde parámetro:", role);
    console.log("🔍 [ModulesRegistry] userRole final:", userRole);
    console.log("🔍 [ModulesRegistry] orgId desde parámetro:", orgId);
    console.log("🔍 [ModulesRegistry] orgModules.length:", orgModules.length);
    console.log("🔍 [ModulesRegistry] ¿Es super_user?:", userRole === 'super_user');

    // Super user has access to everything - check this FIRST before any other validation
    if (userRole === 'super_user') {
      console.log(`✅ [ModulesRegistry] Super user detected, granting access to module: ${moduleCode || 'all'}`);
      return true;
    }
    
    console.log(`⚠️ [ModulesRegistry] Usuario NO es super_user (rol: ${userRole}), continuando con validaciones normales`);

    // If no module code specified, deny access for non-super_user
    if (!moduleCode) {
      console.warn(`[ModulesRegistry] isModuleAuthorized called with undefined moduleCode for org ${orgId || 'unknown'}`);
      return false;
    }

    // For super users, we already returned true above, so this check is only for non-super users
    // However, if no modules are loaded and user is not super_user, we need to check if they should have access
    // For admin and user roles, they need org modules loaded
    if (!orgModules.length && userRole !== 'super_user') {
      console.warn(`[ModulesRegistry] isModuleAuthorized: No org modules loaded for org ${orgId || 'unknown'}. Current orgModules length: ${orgModules.length}`);
      // If user has modules assigned but org modules aren't loaded yet, allow access based on user modules
      if (state.userModules.length > 0) {
        const userModule = state.userModules.find(m => m.code === moduleCode);
        if (userModule) {
          console.log(`[ModulesRegistry] Found module in userModules, allowing access`);
          return true;
        }
      }
      return false;
    }

    // Helper to find module info by code or alias
    const getModuleInfo = (code: string): Module | undefined => {
      // First try direct match
      let moduleInfo = orgModules.find(m => m.code === code);

      if (!moduleInfo) {
        // Search by aliases
        for (const [mainCode, aliases] of Object.entries(moduleAliases)) {
          // If requested code is an alias, find main module
          if (aliases.includes(code)) {
            moduleInfo = orgModules.find(m => m.code === mainCode);
            if (moduleInfo) break;
          }

          // If requested code is main code, search in its aliases
          if (mainCode === code) {
            for (const alias of aliases) {
              moduleInfo = orgModules.find(m => m.code === alias);
              if (moduleInfo) break;
            }
            if (moduleInfo) break;
          }
        }
      }

      return moduleInfo;
    };

    // Special case for tables_management
    if (moduleCode === 'tables_management' || 
        (moduleAliases.tables_management && moduleAliases.tables_management.includes(moduleCode))) {
      if (userRole === 'admin' || userRole === 'super_user') {
        return true;
      }
    }

    // Check if module exists directly or by alias
    const moduleInfo = getModuleInfo(moduleCode);

    // If module not in organization modules, deny access
    if (!moduleInfo) {
      // Exception for tables_management
      if (moduleCode === 'tables_management' || 
          (moduleAliases.tables_management && moduleAliases.tables_management.includes(moduleCode))) {
        if (userRole === 'admin' || userRole === 'super_user') {
          return true;
        }
      }
      return false;
    }

    // Regular users only get EXPLICITLY ASSIGNED modules
    // Check if module is in userModules (explicitly assigned to the user)
    if (userRole === 'user') {
      // Check if module is explicitly assigned to the user
      const isAssigned = state.userModules.some(m => {
        const normalizedCode = m.code?.toLowerCase() || '';
        const requestedCode = moduleCode.toLowerCase();
        
        // Direct match
        if (normalizedCode === requestedCode) {
          return true;
        }
        
        // Check aliases
        for (const [mainCode, aliases] of Object.entries(moduleAliases)) {
          if ((normalizedCode === mainCode || aliases.includes(normalizedCode)) &&
              (requestedCode === mainCode || aliases.includes(requestedCode))) {
            return true;
          }
        }
        
        return false;
      });
      
      // Only allow if explicitly assigned (not just because it's basic)
      console.log(`[ModulesRegistry] User module check: ${moduleCode} is ${isAssigned ? 'assigned' : 'NOT assigned'}`);
      return isAssigned;
    }

    // Admins get all modules in their organization
    if (userRole === 'admin') {
      return true;
    }

    // Default: deny access
    return false;
  },

  /**
   * Invalidate user modules cache (force refresh on next loadModules call)
   * Use this after creating, updating, or deleting modules
   */
  invalidateUserModules() {
    state.listMeta = null;
    state.userModules = [];
    state.orgModules = [];
    notify();
  },

  /**
   * Invalidate organization modules cache for a specific organization
   * Use this after assigning/unassigning modules to an organization
   */
  invalidateOrgModules(orgId: number) {
    state.orgModulesCache.delete(orgId);
    // Also invalidate user modules since they depend on org modules for DEMO flag
    state.listMeta = null;
    notify();
  },

  /**
   * Clear all cache (user modules + all org modules)
   * Use this on logout or major context changes
   */
  clearCache() {
    state.userModules = [];
    state.orgModules = [];
    state.listMeta = null;
    state.orgModulesCache.clear();
    state.inFlight.clear();
    notify();
  },
};

export function useModules(options?: { orgId?: number; role?: string; email?: string }) {
  const [snap, setSnap] = useState(ModulesRegistry.getState());
  useEffect(() => {
    return ModulesRegistry.subscribe(() => setSnap(ModulesRegistry.getState()));
  }, []);

  // Memoize refresh function to avoid recreating it on every render
  const refreshFn = useMemo(() => {
    return (force = false) => {
      console.log(`[useModules] refresh() called with force=${force}, options=`, options);
      return ModulesRegistry.loadModules(force, options);
    };
  }, [options?.orgId, options?.role, options?.email]);

  // Optimistic loading: if the desired cache key doesn't match what's cached,
  // a load is pending (useEffect hasn't fired yet). Report loading=true immediately
  // so ProtectedRoute shows a spinner instead of denying access during the race
  // window between a context switch (e.g. impersonation) and the useEffect firing.
  const desiredKey = `${options?.email || 'unknown'}|${options?.orgId || 0}|${options?.role || 'user'}`;
  const cachedKey = snap.listMeta?.cacheKey;
  const pendingLoad = !!options?.email && desiredKey !== cachedKey && !snap.loading;

  return useMemo(() => ({
    userModules: snap.userModules,
    orgModules: snap.orgModules,
    loading: snap.loading || pendingLoad,
    error: snap.error,
    refresh: refreshFn,
    isModuleAuthorized: (moduleCode: string | undefined) => ModulesRegistry.isModuleAuthorized(moduleCode, options?.role, options?.orgId),
  }), [snap, refreshFn, options?.role, options?.orgId, pendingLoad]);
}

